import { Command } from 'cmdk';
import { ArrowLeft, Check, ChevronRight, Loader2, Search, X } from 'lucide-react';
import { Dialog as D, DropdownMenu as M, Popover as P, Tooltip as T } from 'radix-ui';
import { type ReactNode, useState } from 'react';
import { cn } from '../lib/cn';

/* ------------------------------------------------------------------ Tooltip */

export function Tooltip({
  content,
  children,
  side = 'top',
  delayDuration = 300,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delayDuration?: number;
}) {
  if (!content) return <>{children}</>;
  return (
    <T.Provider delayDuration={delayDuration} skipDelayDuration={100}>
      <T.Root>
        <T.Trigger asChild>{children}</T.Trigger>
        <T.Portal>
          <T.Content
            side={side}
            sideOffset={6}
            className="z-[70] max-w-64 rounded-md bg-fg px-2.5 py-1.5 text-xs font-medium text-bg shadow-md data-[state=delayed-open]:animate-fade-in"
          >
            {content}
            <T.Arrow className="fill-fg" />
          </T.Content>
        </T.Portal>
      </T.Root>
    </T.Provider>
  );
}

/* ------------------------------------------------------------------- Dialog */

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

const dialogWidth = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

interface DialogContentBase {
  title: ReactNode;
  description?: ReactNode;
  /** Visually hide the title (it is still announced to screen readers). */
  hideTitle?: boolean;
  children?: ReactNode;
  className?: string;
  onInteractOutside?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
}

interface DialogContentDialogProps extends DialogContentBase {
  presentation?: 'dialog';
  size?: keyof typeof dialogWidth;
  footer?: ReactNode;
}

interface DialogContentRecordProps extends DialogContentBase {
  /** Full-workspace presentation for persistent record create/edit/view forms: always the same fixed width. */
  presentation: 'record';
  /**
   * Rendered in a left-aligned action bar below the title, e.g. Discard/Save. A record form has no separate footer.
   */
  headerActions?: ReactNode;
  /** The back arrow becomes an inert, dimmed button: for a dirty form, where Discard/Save (in `headerActions`) are the only way out. */
  backDisabled?: boolean;
}

export type DialogContentProps = DialogContentDialogProps | DialogContentRecordProps;

export function DialogContent(props: DialogContentProps) {
  const { title, description, hideTitle, children, className, onInteractOutside, onEscapeKeyDown } = props;
  const record = props.presentation === 'record';
  const size = record ? 'md' : (props.size ?? 'md');
  const footer = record ? undefined : props.footer;
  const headerActions = record ? props.headerActions : undefined;
  const backDisabled = record && props.backDisabled;
  return (
    <D.Portal>
      <D.Overlay
        style={record ? { left: 'var(--app-sidebar-width, 0px)', top: '3.5rem' } : undefined}
        className={cn(
          'fixed z-50 overflow-y-auto data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in',
          record ? 'right-0 bottom-0 bg-surface-2' : 'inset-0 grid place-items-center bg-scrim p-4',
        )}
      >
        <D.Content
          onInteractOutside={onInteractOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          className={cn(
            'relative flex w-full flex-col bg-surface outline-none',
            record
              ? 'min-h-full'
              : 'my-auto max-h-[calc(100dvh-2rem)] rounded-xl border shadow-overlay data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in',
            !record && dialogWidth[size],
            className,
          )}
        >
          <header
            className={cn(
              'flex items-start gap-4 px-6',
              record ? 'sticky top-0 z-10 border-b bg-surface py-4 shadow-xs' : hideTitle ? 'pt-5 pb-0' : 'pt-5 pb-4',
            )}
          >
            {record ? (
              backDisabled ? (
                <span aria-hidden className="-ml-2 grid size-9 shrink-0 place-items-center rounded-md text-disabled">
                  <ArrowLeft className="size-4" />
                </span>
              ) : (
                <D.Close
                  aria-label="Back to previous page"
                  className="-ml-2 grid size-9 shrink-0 place-items-center rounded-md text-subtle transition-colors hover:bg-sunken hover:text-fg"
                >
                  <ArrowLeft className="size-4" aria-hidden />
                </D.Close>
              )
            ) : null}
            <div className="min-w-0 flex-1">
              <D.Title className={cn(record ? 'text-xl font-semibold text-fg' : 'text-lg font-semibold text-fg', hideTitle && 'sr-only')}>{title}</D.Title>
              {description ? (
                <D.Description className="mt-1 text-base text-muted">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</D.Description>
              )}
            </div>
            {!record ? (
              <D.Close
                aria-label="Close dialog"
                className="-mr-2 -mt-1 grid size-9 place-items-center rounded-md text-subtle transition-colors hover:bg-sunken hover:text-fg"
              >
                <X className="size-4" aria-hidden />
              </D.Close>
            ) : null}
          </header>
          {record && headerActions ? <div className="flex shrink-0 items-center gap-2 border-b bg-surface-2 px-6 py-3">{headerActions}</div> : null}
          {children ? (
            <div className={cn('min-h-0 flex-1', record ? 'mx-auto w-full max-w-7xl bg-surface px-6 py-6 lg:px-10' : 'overflow-y-auto px-6 pb-5')}>{children}</div>
          ) : null}
          {!record && footer ? <footer className="flex flex-wrap items-center justify-end gap-2 rounded-b-xl border-t bg-surface-2 px-6 py-3">{footer}</footer> : null}
        </D.Content>
      </D.Overlay>
    </D.Portal>
  );
}

/* ------------------------------------------------------------------- Drawer */

const drawerWidth = { sm: 'w-[26rem]', md: 'w-[34rem]', lg: 'w-[46rem]' } as const;

export interface DrawerContentProps {
  title: ReactNode;
  description?: ReactNode;
  size?: keyof typeof drawerWidth;
  /** Use a full workspace for persistent records instead of a quick-action drawer. */
  presentation?: 'drawer' | 'record';
  footer?: ReactNode;
  /** Only used with `presentation="record"` — see `DialogContent`. */
  headerActions?: ReactNode;
  backDisabled?: boolean;
  children?: ReactNode;
  className?: string;
}

/** Right-hand side sheet: quick-create and detail flows that should not leave the current page. */
export function DrawerContent({ title, description, size = 'md', presentation = 'drawer', footer, headerActions, backDisabled, children, className }: DrawerContentProps) {
  if (presentation === 'record') {
    return (
      <DialogContent title={title} description={description} presentation="record" headerActions={headerActions} backDisabled={backDisabled} className={className}>
        {children}
      </DialogContent>
    );
  }
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
      <D.Content
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex max-w-full flex-col border-l bg-surface shadow-overlay outline-none',
          'data-[state=closed]:animate-slide-out-right data-[state=open]:animate-slide-in-right',
          drawerWidth[size],
          className,
        )}
      >
        <header className="flex items-start gap-4 border-b px-6 py-4">
          <div className="min-w-0 flex-1">
            <D.Title className="text-lg font-semibold text-fg">{title}</D.Title>
            {description ? (
              <D.Description className="mt-0.5 text-base text-muted">{description}</D.Description>
            ) : (
              <D.Description className="sr-only">{typeof title === 'string' ? title : 'Panel'}</D.Description>
            )}
          </div>
          <D.Close
            aria-label="Close panel"
            className="-mr-2 grid size-9 place-items-center rounded-md text-subtle transition-colors hover:bg-sunken hover:text-fg"
          >
            <X className="size-4" aria-hidden />
          </D.Close>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? <footer className="flex items-center justify-end gap-2 border-t bg-surface-2 px-6 py-3">{footer}</footer> : null}
      </D.Content>
    </D.Portal>
  );
}

/* ------------------------------------------------------------ ConfirmDialog */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Bullet list of concrete consequences, e.g. "Current data will be replaced". */
  consequences?: string[];
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** Force the user to type this exact text before the confirm button enables (very destructive actions). */
  requireText?: string;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  consequences,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'default',
  requireText,
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const blocked = requireText !== undefined && typed.trim() !== requireText;

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
      setTyped('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (!next) {
          setTyped('');
          setError(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="sm"
        title={title}
        description={description}
        footer={
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => onOpenChange(false)}
              className="inline-flex h-(--control-h) items-center rounded-md border border-border-strong bg-surface px-3.5 text-base font-medium text-fg shadow-xs hover:bg-surface-2 disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={busy || blocked}
              aria-busy={busy}
              onClick={handleConfirm}
              className={cn(
                'inline-flex h-(--control-h) items-center gap-2 rounded-md px-3.5 text-base font-medium shadow-xs disabled:opacity-50',
                tone === 'danger'
                  ? 'bg-danger-solid text-on-danger-solid hover:brightness-95'
                  : 'bg-brand text-on-brand hover:bg-brand-hover',
              )}
            >
              {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {confirmLabel}
            </button>
          </>
        }
      >
        {consequences?.length ? (
          <ul className="mt-1 space-y-1.5 text-base text-muted">
            {consequences.map((c) => (
              <li key={c} className="flex gap-2">
                <span aria-hidden className={cn('mt-2 size-1.5 shrink-0 rounded-full', tone === 'danger' ? 'bg-danger-solid' : 'bg-brand')} />
                {c}
              </li>
            ))}
          </ul>
        ) : null}
        {requireText !== undefined ? (
          <label className="mt-4 block text-sm font-medium text-fg">
            Type <span className="rounded bg-sunken px-1.5 py-0.5 font-mono text-xs">{requireText}</span> to confirm
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1.5 h-(--control-h) w-full rounded-md border border-input bg-surface px-3 text-base focus-visible:border-ring focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
              autoComplete="off"
            />
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger-fg">
            {error}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------- DropdownMenu */

export const DropdownMenu = M.Root;
export const DropdownMenuTrigger = M.Trigger;
export const DropdownMenuGroup = M.Group;
export const DropdownMenuSub = M.Sub;

export function DropdownMenuContent({
  className,
  align = 'end',
  ...props
}: React.ComponentProps<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-[60] min-w-52 overflow-hidden rounded-lg border bg-surface p-1 shadow-lg data-[state=open]:animate-slide-down',
          className,
        )}
        {...props}
      />
    </M.Portal>
  );
}

const menuItem =
  'relative flex h-9 cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 text-base text-fg outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-sunken [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-subtle';

export function DropdownMenuItem({
  className,
  icon,
  shortcut,
  destructive,
  children,
  ...props
}: React.ComponentProps<typeof M.Item> & { icon?: ReactNode; shortcut?: string; destructive?: boolean }) {
  return (
    <M.Item
      className={cn(menuItem, destructive && 'text-danger-fg data-[highlighted]:bg-danger-bg [&_svg]:text-danger-fg', className)}
      {...props}
    >
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {shortcut ? <kbd className="ml-4 font-sans text-xs text-subtle">{shortcut}</kbd> : null}
    </M.Item>
  );
}

export function DropdownMenuCheckboxItem({ className, children, ...props }: React.ComponentProps<typeof M.CheckboxItem>) {
  return (
    <M.CheckboxItem className={cn(menuItem, 'pl-8', className)} {...props}>
      <span className="absolute left-2.5 grid size-4 place-items-center">
        <M.ItemIndicator>
          <Check className="size-4 !text-brand-text" aria-hidden />
        </M.ItemIndicator>
      </span>
      {children}
    </M.CheckboxItem>
  );
}

export function DropdownMenuLabel({ className, ...props }: React.ComponentProps<typeof M.Label>) {
  return <M.Label className={cn('px-2.5 pb-1 pt-2 text-xs font-medium text-subtle', className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof M.Separator>) {
  return <M.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
}

export function DropdownMenuSubTrigger({ children, className, ...props }: React.ComponentProps<typeof M.SubTrigger>) {
  return (
    <M.SubTrigger className={cn(menuItem, className)} {...props}>
      <span className="flex-1">{children}</span>
      <ChevronRight aria-hidden />
    </M.SubTrigger>
  );
}

export function DropdownMenuSubContent({ className, ...props }: React.ComponentProps<typeof M.SubContent>) {
  return (
    <M.Portal>
      <M.SubContent className={cn('z-[60] min-w-48 rounded-lg border bg-surface p-1 shadow-lg', className)} {...props} />
    </M.Portal>
  );
}

/* ------------------------------------------------------------------ Popover */

export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;

export function PopoverContent({ className, align = 'start', sideOffset = 6, ...props }: React.ComponentProps<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-[60] rounded-lg border bg-surface p-1 shadow-lg outline-none data-[state=open]:animate-slide-down',
          className,
        )}
        {...props}
      />
    </P.Portal>
  );
}

/* ---------------------------------------------------------- CommandPalette */

export interface CommandItemData {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  /** Small right-aligned tag, e.g. "Athlete" or "Active". */
  meta?: string;
  keywords?: string[];
  onSelect: () => void;
}

export interface CommandGroupData {
  heading: string;
  items: CommandItemData[];
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: CommandGroupData[];
  query: string;
  onQueryChange: (value: string) => void;
  loading?: boolean;
  placeholder?: string;
  emptyText?: string;
  /** Client-side filtering (default). Turn off when `groups` already reflect a server search. */
  filter?: boolean;
}

export function CommandPalette({
  open,
  onOpenChange,
  groups,
  query,
  onQueryChange,
  loading,
  placeholder = 'Search athletes, teams, injuries…',
  emptyText = 'No results found.',
  filter = false,
}: CommandPaletteProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <D.Content
          aria-label="Search"
          className="fixed left-1/2 top-[14vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border bg-surface shadow-overlay outline-none data-[state=open]:animate-slide-down"
        >
          <D.Title className="sr-only">Global search</D.Title>
          <D.Description className="sr-only">Search athletes, teams and injuries. Use arrow keys to move and Enter to open.</D.Description>
          <Command shouldFilter={filter} loop label="Global search">
            <div className="flex items-center gap-2.5 border-b px-4">
              <Search className="size-4 shrink-0 text-subtle" aria-hidden />
              <Command.Input
                value={query}
                onValueChange={onQueryChange}
                placeholder={placeholder}
                className="h-12 flex-1 bg-transparent text-md text-fg outline-none placeholder:text-subtle"
              />
              {loading ? <Loader2 className="size-4 animate-spin text-subtle" aria-label="Searching" /> : null}
              <kbd className="rounded border bg-surface-2 px-1.5 py-0.5 font-sans text-xs text-subtle">Esc</kbd>
            </div>
            <Command.List className="max-h-[min(24rem,60vh)] overflow-y-auto p-2">
              <Command.Empty className="px-3 py-8 text-center text-base text-muted">{emptyText}</Command.Empty>
              {groups.map((group) => (
                <Command.Group
                  key={group.heading}
                  heading={group.heading}
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-subtle"
                >
                  {group.items.map((item) => (
                    <Command.Item
                      key={item.id}
                      value={`${item.id} ${item.label} ${(item.keywords ?? []).join(' ')}`}
                      onSelect={() => {
                        onOpenChange(false);
                        item.onSelect();
                      }}
                      className="flex cursor-default items-center gap-3 rounded-lg px-3 py-2 text-base text-fg outline-none data-[selected=true]:bg-brand-subtle"
                    >
                      {item.icon ? <span className="grid size-8 shrink-0 place-items-center rounded-md bg-sunken text-muted [&_svg]:size-4">{item.icon}</span> : null}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{item.label}</span>
                        {item.description ? <span className="block truncate text-sm text-muted">{item.description}</span> : null}
                      </span>
                      {item.meta ? <span className="shrink-0 text-xs text-subtle">{item.meta}</span> : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
            <div className="flex items-center gap-4 border-t bg-surface-2 px-4 py-2 text-xs text-subtle">
              <span>
                <kbd className="font-sans">↑↓</kbd> navigate
              </span>
              <span>
                <kbd className="font-sans">Enter</kbd> open
              </span>
            </div>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
